import type { QueryClient } from "@tanstack/react-query";
import { createRouter, type RouterHistory } from "@tanstack/react-router";
import { rootRoute } from "~/app/router/rootRoute";
import { homeRoute } from "~/app/router/routes/homeRoute";
import { scanDmcRoute } from "~/app/router/routes/scanDmcRoute";
import { selectionAssistantRoute } from "~/app/router/routes/selectionAssistantRoute";
import { housingSelectionAssistantRoute } from "~/app/router/routes/housingSelectionAssistantRoute";
import { storeFinderConfirmationRoute } from "~/app/router/routes/storeFinderConfirmationRoute";
import { storeFinderRoute } from "~/app/router/routes/storeFinderRoute";
import { linearConfigurationRequestQuoteConfirmationRoute } from "~/app/router/routes/linearConfigurationRequestQuoteConfirmationRoute";
import { shareLinearConfigurationsConfirmationRoute } from "~/app/router/routes/shareLinearConfigurationsConfirmationRoute";
import { shareWishlistConfirmationRoute } from "~/app/router/routes/shareWishlistConfirmationRoute";
import { registeredUserRoute } from "~/app/router/routes/registeredUserRoute";
import { registeredUserConfirmationRoute } from "~/app/router/routes/registeredUserConfirmationRoute";
import { lubricationSelectionAssistantRoute } from "~/app/router/routes/lubricationSelectionAssistantRoute";
import { contactUsRoute } from "~/app/router/routes/contactUsRoute";
import { contractDetailsRoute } from "~/app/router/routes/contractDetailsRoute";
import { contractOverviewRoute } from "~/app/router/routes/contractOverviewRoute";
import { orderHistoryRoute } from "~/app/router/routes/orderHistoryRoute";
import { orderDetailRoute } from "~/app/router/routes/orderDetailRoute";
import { myAccountDashboardRoute } from "~/app/router/routes/myAccountDashboardRoute";
import { accountSettingsRoute } from "~/app/router/routes/accountSettingsRoute";
import { orderAndQuoteSettingsRoute } from "~/app/router/routes/orderAndQuoteSettingsRoute";
import { newContractRoute } from "~/app/router/routes/newContractRoute";
import { cartRoute } from "~/app/router/routes/cartRoute";
import { cartStoreFinderRoute } from "~/app/router/routes/cartStoreFinderRoute";
import { cartRequestRoute } from "~/app/router/routes/cartRequestRoute";
import { quoteCartRoute } from "~/app/router/routes/quoteCartRoute";
import { quoteConfirmationRedirectRoute, quoteConfirmationRoute } from "~/app/router/routes/quoteConfirmationRoute";
import { stcRoute } from "~/app/router/routes/stcRoute";
import { wishlistRoute } from "~/app/router/routes/wishlistRoute";
import { savedConfigurationsRoute } from "~/app/router/routes/savedConfigurationsRoute";
import { digitalServiceRoute } from "~/app/router/routes/digitalServiceRoute";
import { notificationsRoute } from "~/app/router/routes/notificationsRoute";
import { notificationSettingsRoute } from "~/app/router/routes/notificationSettingsRoute";
import { passwordChangeRoute } from "~/app/router/routes/passwordChangeRoute";
import { manageUserCreateRoute, manageUserEditRoute } from "~/app/router/routes/manageUserFormRoute";
import { manageUsersRoute } from "~/app/router/routes/manageUsersRoute";
import { quoteSearchRoute } from "~/app/router/routes/quoteSearchRoute";
import { quoteDetailRoute } from "~/app/router/routes/quoteDetailRoute";
import { mediaLibraryRoute } from "~/app/router/routes/mediaLibraryRoute";
import { selfRegistrationRoute } from "~/app/router/routes/selfRegistrationRoute";
import { abMonitorRoute } from "~/app/router/routes/abMonitorRoute";
import { selectAccountRoute } from "~/app/router/routes/selectAccountRoute";
import { freeCapacityCockpitRoute } from "~/app/router/routes/freeCapacityCockpitRoute";
import { salesCampaignRoute } from "~/app/router/routes/salesCampaignRoute";
import { checkoutConfirmationRoute } from "~/app/router/routes/checkoutConfirmationRoute";
import { checkoutSummaryRoute } from "~/app/router/routes/checkoutSummaryRoute";
import { categoryRoute } from "~/app/router/routes/categoryRoute";
import { plpRoute } from "~/app/router/routes/plpRoute";
import { shortPdpRoute } from "~/app/router/routes/productRoute";
import { searchAjaxRoute, searchRoute } from "~/app/router/routes/searchRoute";
import { datasheetRoute } from "~/app/router/routes/datasheetRoute";
import { bearingFrequencyCalculatorRoute } from "~/app/router/routes/bearingFrequencyCalculatorRoute";
import { forkPageRoute } from "~/app/router/routes/forkPageRoute";
import { previewContentRoute } from "~/app/router/routes/previewContentRoute";
import {
  configuratorRoute,
  railsConfiguratorRoute,
  carriagesConfiguratorRoute,
  shaftConfiguratorRoute,
} from "~/app/router/routes/configuratorRoute";
import { embeddedConfiguratorRoute } from "~/app/router/routes/embeddedConfiguratorRoute";
import { catchAllRoute } from "~/app/router/routes/catchAllRoute";
import { mediaLibrarySdsRoute } from "./routes/mediaLibrarySdsRoute";

const routeTree = rootRoute.addChildren([
  homeRoute,
  scanDmcRoute,
  categoryRoute,
  plpRoute,
  shortPdpRoute,
  searchRoute,
  searchAjaxRoute,
  datasheetRoute,
  selectionAssistantRoute,
  housingSelectionAssistantRoute,
  lubricationSelectionAssistantRoute,
  storeFinderConfirmationRoute,
  storeFinderRoute,
  linearConfigurationRequestQuoteConfirmationRoute,
  shareLinearConfigurationsConfirmationRoute,
  shareWishlistConfirmationRoute,
  registeredUserRoute,
  registeredUserConfirmationRoute,
  contactUsRoute,
  cartRoute,
  cartStoreFinderRoute,
  cartRequestRoute,
  quoteCartRoute,
  quoteConfirmationRedirectRoute,
  quoteConfirmationRoute,
  stcRoute,
  wishlistRoute,
  savedConfigurationsRoute,
  digitalServiceRoute,
  notificationsRoute,
  notificationSettingsRoute,
  passwordChangeRoute,
  manageUserEditRoute,
  manageUserCreateRoute,
  manageUsersRoute,
  quoteSearchRoute,
  quoteDetailRoute,
  mediaLibraryRoute,
  mediaLibrarySdsRoute,
  selfRegistrationRoute,
  abMonitorRoute,
  selectAccountRoute,
  freeCapacityCockpitRoute,
  salesCampaignRoute,
  checkoutConfirmationRoute,
  checkoutSummaryRoute,
  contractDetailsRoute,
  contractOverviewRoute,
  orderHistoryRoute,
  orderDetailRoute,
  myAccountDashboardRoute,
  accountSettingsRoute,
  orderAndQuoteSettingsRoute,
  newContractRoute,
  bearingFrequencyCalculatorRoute,
  forkPageRoute,
  previewContentRoute,
  configuratorRoute,
  railsConfiguratorRoute,
  carriagesConfiguratorRoute,
  shaftConfiguratorRoute,
  embeddedConfiguratorRoute,
  catchAllRoute,
]);

export function createStorefrontRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    basepath: window.__SCHA_BOOT__?.contextPath || "/",
    context: {
      queryClient,
    },
    history,
  });
}
